import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import Layout from "../components/Layout";
import contractRequestService from "../services/contract-request.service";

// #40 - Principal/agency countersignature page. In production it hands the
// principal to the real DocuSign embedded Assignor signing session and finalizes
// when they return; outside production it uses a stand-in signing action. Either
// way, completing it clears the hold and creates the held Freshservice ticket(s).
const Countersign = () => {
  const { id } = useParams();
  const [params] = useSearchParams();
  const returned = params.get("event") === "complete";

  const [loading, setLoading] = useState(true);
  const [mock, setMock] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState(null);

  const finalize = () => {
    setBusy(true);
    setError(null);
    return contractRequestService
      .countersign(id)
      .then(() => setDone(true))
      .catch((err) => {
        if (err?.response?.status === 409) {
          setError("The signature has not been received yet. Please finish signing, then try again.");
        } else {
          setError("Could not complete the countersignature. Please try again.");
        }
      })
      .finally(() => setBusy(false));
  };

  useEffect(() => {
    // Coming back from the real DocuSign session: complete the hold release.
    if (returned) {
      setLoading(false);
      finalize();
      return;
    }
    contractRequestService
      .countersignUrl(id)
      .then((data) => {
        if (data?.signingUrl) {
          window.location.href = data.signingUrl; // real DocuSign embedded view
          return;
        }
        setMock(!!data?.mock);
        setLoading(false);
      })
      .catch(() => {
        setError("Could not start the countersignature. Please try again.");
        setLoading(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, returned]);

  return (
    <Layout>
      <Box sx={{ maxWidth: 600, mx: "auto", mt: 4 }}>
        <Paper sx={{ p: 3 }}>
          <Typography variant="h5" gutterBottom>
            Countersign this agent's DocuSign
          </Typography>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            This request is on hold until you, the principal/agency, complete your
            portion of the DocuSign (LOA/AOC). Sign below to submit it back to AGA.
          </Typography>

          {done ? (
            <Alert severity="success" sx={{ mt: 2 }}>
              Thank you. The request has been completed and is now being processed.
            </Alert>
          ) : loading ? (
            <Box sx={{ mt: 3, textAlign: "center" }}>
              <CircularProgress size={24} />
            </Box>
          ) : (
            <Stack spacing={2} sx={{ mt: 2 }}>
              {error && <Alert severity="error">{error}</Alert>}
              {mock && (
                <Button
                  variant="contained"
                  disabled={busy}
                  onClick={finalize}
                  startIcon={busy ? <CircularProgress size={18} /> : null}
                >
                  Sign and submit
                </Button>
              )}
            </Stack>
          )}
        </Paper>
      </Box>
    </Layout>
  );
};

export default Countersign;
